import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MdVerified } from "react-icons/md";
import { FaShareFromSquare } from "react-icons/fa6";

interface Campaign {
  id: number;
  title: string;
  slug: string;
  thumbnail: string;
  target: number;
  collected: number;
  donation_category?: {
    name: string;
  };
  created_by?: {
    name: string;
  };
}

const truncateText = (text: string, max: number) => {
  if (text.length <= max) return text;
  return text.slice(0, max) + "...";
};

const CampaignSection: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedTitle, setExpandedTitle] = useState<number | null>(null);
  const IconVerif = MdVerified as React.FC<React.SVGProps<SVGSVGElement>>;
  const IconShare = FaShareFromSquare as React.FC<React.SVGProps<SVGSVGElement>>;

  const handleShare = (item: Campaign) => {
    if (navigator.share) {
      navigator.share({
        title: item.title,
        text: `Ayo bantu campaign ini: ${item.title}`,
        url: `https://belaskasih.com/campaigns/${item.slug}`,
      });
    } else {
      alert("Fitur share tidak didukung di browser ini");
    }
  };

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const res = await fetch("https://api.belaskasih.com/api/v1/campaign?status=approved&limit=200");
        const json = await res.json();

        if (json?.data?.data) {
          const filtered = json.data.data.filter((item: Campaign) => /sumatra|sumatera|aceh/i.test(item.title));
          setCampaigns(filtered);
        }
      } catch (error) {
        console.error("Gagal mengambil campaign:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  return (
    <section className="bg-[#131010] py-16">
      <div className="max-w-[1440px] mx-auto px-4 md:px-8">
        {/* HEADER */}
        <div className="text-center mb-12 flex flex-col justify-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Donasi <span className="text-orange-400 font-bilbo">Peduli Sumatera</span>
          </h2>
          <p className="mt-2 text-xs md:text-sm max-w-xl mx-auto text-center text-gray-300">
            Pulihkan Senyum Sumatera. Saudara kita di sana sedang kehilangan segalanya. Sedikit bantuan Anda adalah harapan besar bagi mereka untuk bangkit kembali. Mari bantu sekarang.
          </p>
        </div>

        {loading && <p className="text-center text-gray-400">Memuat campaign...</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {campaigns.map((item) => {
            const percentage = Math.min(Math.round((item.collected / item.target) * 100), 100);

            const isExpanded = expandedTitle === item.id;

            return (
              <div
                key={item.id}
                className="relative bg-[#2a2a2a] rounded-2xl overflow-hidden
                           border border-white/10">
                {/* GRADIENT */}
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-400 to-white" />

                {/* IMAGE */}
                <img src={item.thumbnail} alt={item.title} className="w-full h-[190px] object-cover" />

                {/* CONTENT */}
                <div className="p-5 text-white flex flex-col h-full">
                  {/* TITLE (TOGGLE) */}
                  <h3 onClick={() => setExpandedTitle(isExpanded ? null : item.id)} className="text-lg font-bold mb-2 cursor-pointer hover:text-orange-400 transition" title={item.title}>
                    {isExpanded ? item.title : truncateText(item.title, 30)}
                  </h3>

                  {/* created by */}
                  <div className="  text-gray-300 text-sm italic  mb-3 flex justify-start items-center gap-2">
                    {item.created_by?.name ?? "-"}
                    <span className="text-primary">
                      <IconVerif />
                    </span>
                  </div>

                  {/* PROGRESS TEXT */}
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-primary font-semibold">Rp {item.collected.toLocaleString("id-ID")}</span>
                    <span className="text-gray-300">{percentage}%</span>
                  </div>

                  {/* PROGRESS BAR */}
                  <div className="w-full h-2 bg-gray-600 rounded-full mb-3">
                    <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${percentage}%` }} />
                  </div>

                  {/* TARGET */}
                  <div className="flex justify-between items-center mb-6">
                    <p className="text-sm text-gray-300 font-sans italic">
                      Target Pengumpulan: <span className="text-white font-semibold">Rp {item.target.toLocaleString("id-ID")}</span>
                    </p>

                    <button onClick={() => handleShare(item)} className="hover:text-orange-400 text-primary transition" title="Bagikan Campaign">
                      <span className="text-[18px]">
                        <IconShare />
                      </span>
                    </button>
                  </div>

                  <div className="flex justify-center">
                    <Link to={`https://belaskasih.com/campaigns/${item.slug}`} target="_blank" rel="noopener noreferrer" className="mt-auto">
                      <div className="px-11 border border-white text-white rounded-lg py-2 text-sm font-semibold hover:bg-primary hover:text-white transition">Donasi Sekarang</div>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CampaignSection;
